import {proxy, ref} from "valtio";

import type {DestinationCardProps} from "@/entities/destination/ui/card/destination-card";

import {EventEmitter} from "@/shared/event-emitter";
import {IValtioModel} from "@/shared/models/base-model.interface";

export interface DestinationListConfig<T = DestinationCardProps> {
    destinations: Array<T>;
    itemsPerPage?: number;
}

interface DestinationListEvents {
    scrollToButton: (callback?: () => void) => void;
    scrollToTop: (callback?: () => void) => void;
}

class DestinationListClass<T = DestinationCardProps> implements IValtioModel {
    // Reactive state
    visibleCount: number;
    destinations: Array<T>;

    itemsPerPage: number;
    public readonly events: EventEmitter<DestinationListEvents>;

    constructor(config: DestinationListConfig<T>) {
        this.itemsPerPage = config.itemsPerPage ?? 4;
        this.destinations = [...config.destinations];
        this.visibleCount = this.itemsPerPage;
        this.events = ref(new EventEmitter<DestinationListEvents>()) as EventEmitter<DestinationListEvents>;
    }

    // Computed getters
    get totalItems(): number {
        return this.destinations.length;
    }

    get visibleDestinations(): Array<T> {
        return this.destinations.slice(0, this.visibleCount);
    }

    get hasMore(): boolean {
        return this.visibleCount < this.totalItems;
    }

    get showButton(): boolean {
        return this.totalItems > this.itemsPerPage;
    }

    setItemsPerPage(count: number): void {
        const wasAtInitial = this.visibleCount === this.itemsPerPage;
        this.itemsPerPage = count;
        if (wasAtInitial) {
            this.visibleCount = count;
        }
    }

    // Actions
    handleShowMore(): void {
        if (this.hasMore) {
            this.loadMore();
        } else {
            this.showLess();
        }
    }

    loadMore(): void {
        this.visibleCount = this.visibleCount + this.itemsPerPage;
        this.scrollLastItem();
    }

    showLess(): void {
        this.events.emit("scrollToTop", () => {
            this.visibleCount = this.itemsPerPage;
        });
    }

    private scrollLastItem(): void {
        requestAnimationFrame(() => {
            this.events.emit("scrollToButton");
        });
    }

    showAll(): void {
        this.visibleCount = this.totalItems;
        this.scrollLastItem();
    }
    shuffle(): void {
        const shuffled = [...this.destinations];
        for (let i = shuffled.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        this.destinations = shuffled;
        this.visibleCount = this.itemsPerPage;
    }

    init(): void | Promise<void> {
        /* empty */
    }
}

// Export type
export type DestinationListInstance<T = DestinationCardProps> = DestinationListClass<T>;

// Factory function (SSR compatible)
export function DestinationList<T = DestinationCardProps>(config: DestinationListConfig<T>): DestinationListInstance<T> {
    return proxy<DestinationListInstance<T>>(new DestinationListClass<T>(config));
}
